import type { MessageKey } from "@colorglo/i18n";
import {
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  ScrollArea,
  Separator,
  Skeleton,
} from "@colorglo/ui/components";
import {
  iconSize,
  NavNotifications,
  NavNotificationsUnread,
  SettingsNotifications,
} from "@colorglo/ui/icons";
import { Link } from "@tanstack/react-router";
import { useEffect, useRef } from "react";
import {
  type StaffNotificationView,
  useMarkNotificationsRead,
  useNotificationPreferences,
  useStaffNotifications,
} from "../../api/staff-notifications.ts";
import { usePlural, useT } from "../../lib/locale.ts";
import { armSound, playChime } from "../../lib/sound.ts";
import { NotificationEntry } from "./entry.tsx";

/**
 * The bell in the header.
 *
 * ── Why the actions are here and not only on the job card ────────────────────
 *
 * A booking that landed `pending` is a customer waiting for an answer, and the shortest
 * honest path from "somebody booked" to "we said yes" is two clicks in the thing that told
 * you. The job card still has the full picture; this has the two buttons that are the whole
 * decision. They post to the same `POST …/status` endpoint the calendar does, so the state
 * machine, the audit row and the customer's confirmation email are identical either way.
 *
 * ── Why "reject" is a cancel and not a status of its own ─────────────────────
 *
 * `appointment_status` has no `rejected`. Adding one would be a migration, a new branch in
 * the state machine and a sixth email template, to record a distinction the customer does
 * not experience: from their side, they asked and the answer was no. So reject is
 * `pending → cancelled` carrying a reason, which the state machine already allows and the
 * cancellation template already explains. If HQ later wants to count refusals separately,
 * `cancel_reason` is where that number already lives.
 */
export function NotificationBell({
  franchiseId,
  enabled,
}: {
  readonly franchiseId: string | null;
  readonly enabled: boolean;
}) {
  const t = useT();
  const plural = usePlural();
  const feed = useStaffNotifications(franchiseId, enabled);
  const markRead = useMarkNotificationsRead(franchiseId ?? "");

  useChimeOnArrival(feed.data?.data ?? [], feed.data?.unread_count ?? 0, feed.isSuccess);

  if (!enabled || franchiseId === null) return null;

  const unread = feed.data?.unread_count ?? 0;
  const awaiting = feed.data?.awaiting_count ?? 0;
  const entries = feed.data?.data ?? [];
  const Glyph = unread > 0 ? NavNotificationsUnread : NavNotifications;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          aria-label={
            unread > 0 ? `${t("bell.open")} — ${plural("bell.unread", unread)}` : t("bell.open")
          }
        >
          <Glyph {...iconSize.control} aria-hidden="true" />
          {unread > 0 && (
            <span
              aria-hidden="true"
              className="absolute -top-0.5 -right-0.5 flex min-w-4 items-center justify-center rounded-full bg-brand-strong px-1 text-[10px] font-medium leading-4 text-on-brand"
            >
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </Button>
      </PopoverTrigger>

      <PopoverContent align="end" sideOffset={8} className="w-[min(24rem,calc(100vw-2rem))] p-0">
        <header className="flex items-center justify-between gap-2 px-4 py-3">
          <div className="grid min-w-0">
            <span className="font-medium">{t("bell.title")}</span>
            {awaiting > 0 && (
              <span className="truncate text-caption text-muted-foreground">
                {plural("bell.awaiting", awaiting)}
              </span>
            )}
          </div>
          <Link
            to="/installningar/aviseringar"
            search={{ franchise: franchiseId }}
            aria-label={t("bell.settings")}
            title={t("bell.settings")}
            className="rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-accent-foreground"
          >
            <SettingsNotifications {...iconSize.inline} aria-hidden="true" />
          </Link>
        </header>

        <Separator />

        {feed.isPending ? (
          <div className="flex flex-col gap-3 p-4">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        ) : feed.isError ? (
          <p className="px-4 py-6 text-body-sm text-muted-foreground">{t("bell.error")}</p>
        ) : entries.length === 0 ? (
          <div className="flex flex-col gap-1 px-4 py-8 text-center">
            <p className="text-body-sm font-medium">{t("bell.empty")}</p>
            <p className="text-caption text-muted-foreground">{t("bell.empty_hint")}</p>
          </div>
        ) : (
          <ScrollArea className="max-h-[60svh]">
            <ul className="divide-y divide-border">
              {entries.map((entry) => (
                <NotificationEntry
                  key={entry.id}
                  entry={entry}
                  franchiseId={franchiseId}
                  timezone={feed.data?.timezone ?? "Europe/Stockholm"}
                />
              ))}
            </ul>
          </ScrollArea>
        )}

        {unread > 0 && (
          <>
            <Separator />
            <div className="p-2">
              <Button
                variant="ghost"
                size="sm"
                className="w-full"
                disabled={markRead.isPending}
                onClick={() => markRead.mutate([])}
              >
                {t("bell.mark_all_read")}
              </Button>
            </div>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}

/**
 * Chime, and optionally raise a desktop notification, when a new entry appears.
 *
 * Keyed on the newest entry's id rather than on the unread count: the count also moves when
 * somebody marks things read, and a chime that fires on your own click is the fastest way to
 * teach a workshop to turn the sound off. `null` on the first load means an open tab does not
 * announce the backlog it was already carrying.
 *
 * Muting is read from the preferences the person saved; a muted kind still reaches the list,
 * because "do not interrupt me" and "do not tell me" are different requests.
 */
function useChimeOnArrival(
  entries: readonly StaffNotificationView[],
  unread: number,
  loaded: boolean,
) {
  const preferences = useNotificationPreferences();
  const t = useT();
  const plural = usePlural();
  const newest = entries[0];
  const seen = useRef<string | null>(null);
  const primed = useRef(false);

  useEffect(() => armSound(), []);

  useEffect(() => {
    // Priming is about the *fetch* having happened, not about it having returned anything.
    // Keyed on `newest` it swallowed the first notification a quiet workshop ever received:
    // an empty feed left `primed` false, and the booking that arrived after it was spent
    // setting the flag instead of ringing the bell.
    if (!loaded) return;

    if (!primed.current) {
      primed.current = true;
      seen.current = newest?.id ?? null;
      return;
    }

    if (newest === undefined) return;

    const previous = seen.current;
    seen.current = newest.id;

    if (previous === newest.id || newest.read) return;

    const settings = preferences.data?.preferences;
    if (settings === undefined || settings.muted_kinds.includes(newest.kind)) return;

    if (settings.sound_enabled) void playChime();

    if (
      settings.desktop_enabled &&
      typeof Notification !== "undefined" &&
      Notification.permission === "granted" &&
      document.visibilityState === "hidden"
    ) {
      // Tagged with the entry id so a burst of three bookings is three notifications and a
      // re-render of the same one replaces rather than repeats.
      new Notification(t(`bell.kind.${newest.kind}` as MessageKey), {
        body: plural("bell.unread", unread),
        tag: newest.id,
      });
    }
  }, [loaded, newest, plural, preferences.data, t, unread]);
}
